/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import {
  Flex,
  Button,
  Divider,
  Tabs,
  Card,
  Empty,
  Typography,
  Table,
  Avatar,
  Tag,
  Modal,
  DescriptionsProps,
  Descriptions,
  Dropdown,
} from 'antd';
import MiniCustomCard from 'components/bank/AGBBank/CustomCard/MiniCard';
import { customCardColors } from 'constants/Colors/cardColors';
import urls from 'routes/urls';
import AccountDetails from '../AccountDetails';
import GetStatement from '../GetStatement';
import TransactionLimits from '../TransactionLimits';
import { useLanguage } from 'hooks/Language/useLanguage';
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  RightOutlined,
  CheckOutlined,
  CloseOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { maxSize } from 'constants/screenSize';
import useMediaQuery, {
  ScreenType,
} from 'hooks/styleMediaQuery/useMediaQuery';
import TransferToMNOPageContainer from 'containers/bank/AGBBank/QuickTransfer/TransferToMNO';
import InternationalTransferContainer from 'containers/bank/AGBBank/Transfer/InternationalTransfer';
import LocalTransferContainer from 'containers/bank/AGBBank/Transfer/LocalTransfer';
import AccountInsights from '../AccountInsights';
import CashWithdrawContainer from 'containers/bank/AGBBank/Accounts/CashWithdraw';
import {
  ArrowDown,
  ArrowUp,
  Coins,
  FileClock,
  MoreVertical,
} from 'lucide-react';
import { NO_DATA_ICON } from 'constants/general';

import './index.scss';

interface AccountRecord {
  AccountNumber: string;
  AccountName: string;
  Currency: string;
  Balance: string;
  [key: string]: any;
}

interface AccountProps {
  accountNumbers: AccountRecord[];
  userData: any;
  getStatement: {
    accountBalanceData: Array<{ Balance: string }>;
    loadTransaction: boolean;
  };
  updateTransactionLimitOption: any;
  MonthName: string;
  isInSightBalanceVisible: boolean;
  onHandleOpenModal: () => void;
  handleToggleHideInsight: () => void;
  transactionSummaryData: {
    TotalAmountIn: number;
    TotalAmountOut: number;
    AccountCurrency: string;
  };
  filterTransactionsData: any[];
  loadTransaction: any;
  onHandleBackTo: () => void;
  selectedAccount: any;
  setSelectedAccount: any;
}

const { Text } = Typography;

const Account: React.FC<AccountProps> = ({
  accountNumbers,
  userData,
  getStatement,
  updateTransactionLimitOption,
  MonthName,
  isInSightBalanceVisible,
  onHandleOpenModal,
  handleToggleHideInsight,
  transactionSummaryData,
  filterTransactionsData,
  selectedAccount,
  setSelectedAccount,
}) => {
  const { translate, formatDate } = useLanguage();
  const navigate = useNavigate();
  const screen = useMediaQuery();
  const [openGetStatement, setOpenGetStatement] =
    useState<boolean>(false);
  const screenType = screen as ScreenType;
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [selectedCard, setSelectedCard] = useState(accountNumbers);
  const [openAccountDetails, setOpenAccountDetails] =
    useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState({
    drawerTitle: '',
    open: false,
  });

  const { accountBalanceData, loadTransaction } = getStatement;

  const handleCloseOpenDrawer = () => {
    setIsDrawerOpen({
      drawerTitle: '',
      open: false,
    });
  };

  const handleOpenDrawer = (drawerTitle: string) => {
    setIsDrawerOpen({
      open: true,
      drawerTitle: drawerTitle,
    });
  };

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const actions = [
    {
      key: 'report',
      label: translate('Transaction Report'),
      icon: <FileClock strokeWidth={1} />,
      onClick: () => navigate(urls.transactions),
    },
    // {
    //   key: 'withdraw',
    //   label: translate('Agent Cash Withdrawal'),
    //   icon: <Coins strokeWidth={1} />,
    //   onClick: () => handleOpenDrawer(translate('Withdraw')),
    // },
  ];

  const columns = [
    {
      title: translate('Type'),
      key: 'type',
      width: 50,
      fixed: 'left',
      render: (_: any, record: any) => (
        <Avatar
          style={{
            backgroundColor:
              record?.OpsType === '+' ? '#87d068' : '#d4380d',
          }}
          size={18}
          icon={record?.OpsType === '+' ? <ArrowDown /> : <ArrowUp />}
        />
      ),
    },
    {
      title: translate('Date'),
      key: 'transactionDate',
      dataIndex: 'transactionDate',
      render: (_: any, { transactionDate }) =>
        formatDate(transactionDate),
    },
    {
      title: translate('Description'),
      key: 'description',
      dataIndex: 'description',
    },
    {
      title: translate('Debit'),
      key: 'incomingAmount',
      dataIndex: 'incomingAmount',
    },
    {
      title: translate('Credit'),
      key: 'outgoingAmount',
      dataIndex: 'outgoingAmount',
    },
    {
      title: translate('Status'),
      key: 'status',
      width: '120px',
      render: (_: any, record: any) => {
        const statusColors = {
          '0': 'orange',
          '1': 'success',
          default: 'volcano',
        };

        const statusIcons = {
          '0': <></>,
          '1': <CheckOutlined />,
          default: <CloseOutlined />,
        };

        const color =
          statusColors[record?.transStatus] || statusColors.default;
        const icon =
          statusIcons[record?.transStatus] || statusIcons.default;

        return (
          <Tag
            icon={icon}
            color={color}
            style={{ flex: '1', textAlign: 'center' }}
          >
            {translate(record?.transactionStatus)}
          </Tag>
        );
      },
    },
  ];

  const descripItems: DescriptionsProps['items'] = [
    {
      key: '1',
      label: translate('Account Name'),
      children: selectedAccount?.AccountName,
    },
    {
      key: '2',
      label: translate('Account Number'),
      children: selectedAccount?.AccountNumber,
    },
    {
      key: '3',
      label: translate('Currency'),
      children: selectedAccount?.Currency,
    },
  ];

  const items = accountNumbers.map((account: any) => ({
    label: (
      <MiniCustomCard
        accountName={account.AccountName}
        AccountNumber={account.AccountNumber}
        Currency={account.Currency}
        CardBalance={
          accountBalanceData?.[0]?.Balance || account.Balance || '0'
        }
        CardBackground={customCardColors.volcano.background}
        CardBorderColor={customCardColors.volcano.borderColor}
        showIsActive
        key={account.AccountNumber}
      />
    ),
    key: account.AccountNumber,
    children: (
      <Flex gap="small" vertical>
        <Descriptions
          bordered
          size="small"
          layout="vertical"
          title={
            <Flex justify="space-between">
              <span>{translate('Account details')}</span>
              <div className="tab-actions__mobile">
                <Dropdown
                  menu={{
                    items: actions.map((action) => ({
                      key: action.key,
                      label: action.label,
                      icon: action.icon,
                      onClick: action.onClick,
                    })),
                  }}
                  trigger={['click']}
                >
                  <Button
                    type="primary"
                    icon={<MoreVertical strokeWidth={1} />}
                  >
                    {translate('More')}
                  </Button>
                </Dropdown>
              </div>
            </Flex>
          }
          items={descripItems}
          style={{ width: '100%' }}
        />
        <AccountContent
          account={account}
          columns={columns}
          filterTransactionsData={filterTransactionsData}
          loadTransaction={loadTransaction}
          isInSightBalanceVisible={isInSightBalanceVisible}
          handleToggleHideInsight={handleToggleHideInsight}
          onHandleOpenModal={onHandleOpenModal}
          transactionSummaryData={transactionSummaryData}
          MonthName={MonthName}
          screenType={screenType}
          navigate={navigate}
          translate={translate}
          onOpenAccountDetails={() => setOpenAccountDetails(true)}
          onOpenGetStatement={() => setOpenGetStatement(true)}
        />
      </Flex>
    ),
  }));

  useEffect(() => {
    if (accountNumbers) {
      setSelectedCard(accountNumbers);
    }
  }, [accountNumbers]);

  const renderDrawer = () => {
    const drawerTitle = isDrawerOpen.drawerTitle.toLowerCase();

    switch (drawerTitle) {
      case 'agb to agb':
      case 'between own accounts':
        return (
          <InternationalTransferContainer
            isDrawerOpen={isDrawerOpen}
            handleCloseOpenDrawer={handleCloseOpenDrawer}
          />
        );
      case 'operators':
        return (
          <TransferToMNOPageContainer
            isDrawerOpen={isDrawerOpen}
            handleCloseOpenDrawer={handleCloseOpenDrawer}
          />
        );
      case 'to another bank account':
        return (
          <LocalTransferContainer
            isDrawerOpen={isDrawerOpen}
            handleCloseOpenDrawer={handleCloseOpenDrawer}
          />
        );
      case 'withdraw':
        return (
          <CashWithdrawContainer
            isDrawerOpen={isDrawerOpen}
            handleCloseOpenDrawer={handleCloseOpenDrawer}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Flex gap="large" vertical>
      <Tabs
        defaultActiveKey={accountNumbers[0]?.AccountNumber}
        items={items}
        onChange={(key) => {
          const newAccount = accountNumbers.find(
            (acc) => acc.AccountNumber === key,
          );
          if (newAccount) setSelectedAccount(newAccount);
        }}
        className="responsive-tabs"
        tabBarExtraContent={
          <div className="tab-actions">
            <div className="tab-actions__desktop">
              <Flex align="end" gap="middle" vertical>
                <Button
                  type="primary"
                  onClick={() => navigate(urls.transactions)}
                  icon={<FileClock strokeWidth={1} />}
                >
                  {translate('Transaction Report')}
                </Button>
                {/* <Button
                  type="primary"
                  style={{
                    border: `1px solid ${agbColors.navyBlueColor}`,
                  }}
                  icon={<Coins strokeWidth={1} />}
                  onClick={() =>
                    handleOpenDrawer(translate('Withdraw'))
                  }
                >
                  {translate('Agent Cash Withdrawal')}
                </Button> */}
              </Flex>
            </div>
          </div>
        }
      />
      {renderDrawer()}

      <Modal
        title={translate('Account statement')}
        open={openGetStatement}
        closeIcon={false}
        onCancel={() => setOpenGetStatement(false)}
        footer={
          <Flex justify="flex-start">
            <Button
              variant="filled"
              color="danger"
              onClick={() => setOpenGetStatement(false)}
            >
              {translate('Close')}
            </Button>
          </Flex>
        }
      >
        <GetStatement
          userData={userData}
          cardItem={selectedAccount}
          getStatement={getStatement}
        />
      </Modal>

      <Modal
        title={translate('Account Details')}
        open={openAccountDetails}
        closeIcon={false}
        onCancel={() => setOpenAccountDetails(false)}
        footer={
          <Flex justify="flex-end">
            <Button
              variant="filled"
              color="danger"
              onClick={() => setOpenAccountDetails(false)}
            >
              {translate('Close')}
            </Button>
          </Flex>
        }
      >
        <Flex gap="middle" vertical>
          <Flex gap="small" vertical>
            <Text type="secondary">
              {translate('Account Information')}
            </Text>
            <Card size="small" style={{ width: maxSize }}>
              <AccountDetails
                cardItem={selectedAccount}
                userData={userData}
                MonthName={MonthName}
                isInSightBalanceVisible={isInSightBalanceVisible}
                onHandleOpenModal={onHandleOpenModal}
                handleToggleHideInsight={handleToggleHideInsight}
                transactionSummaryData={transactionSummaryData}
              />
            </Card>
          </Flex>

          <Flex gap="small" vertical>
            <Text type="secondary">
              {translate('Account limits')}
            </Text>
            <Card size="small" style={{ width: maxSize }}>
              <TransactionLimits
                cardItem={userData?.[0]?.TransactionLimits}
                updateTransactionLimitOption={
                  updateTransactionLimitOption
                }
              />
            </Card>
          </Flex>
        </Flex>
      </Modal>
    </Flex>
  );
};

const AccountContent: React.FC<{
  account: AccountRecord;
  columns: any[];
  filterTransactionsData: any[];
  loadTransaction: boolean;
  isInSightBalanceVisible: boolean;
  handleToggleHideInsight: () => void;
  onHandleOpenModal: () => void;
  transactionSummaryData: any;
  MonthName: string;
  screenType: ScreenType;
  navigate: any;
  translate: (_key: string) => string;
  onOpenAccountDetails: () => void;
  onOpenGetStatement: () => void;
}> = ({
  columns,
  filterTransactionsData,
  loadTransaction,
  isInSightBalanceVisible,
  handleToggleHideInsight,
  onHandleOpenModal,
  transactionSummaryData,
  MonthName,
  screenType,
  navigate,
  translate,
  onOpenAccountDetails,
  onOpenGetStatement,
}) => (
  <Flex gap="small" justify="space-between" wrap>
    <Card
      className="border-grey-color resp-account-content"
      style={{ width: screenType === 'mobile' ? '100%' : '73%' }}
    >
      <Flex
        align="end"
        justify="space-between"
        style={{
          width: '100%',
        }}
        vertical={screenType === 'mobile'}
      >
        <Flex
          gap="middle"
          align="center"
          vertical
          style={{
            width: '100%',
          }}
        >
          <Flex gap="small" vertical style={{ width: '100%' }}>
            <Flex
              gap="small"
              vertical={screenType === 'mobile'}
              style={{ width: '100%' }}
            >
              <Flex
                justify="space-between"
                align={screenType === 'mobile' ? 'start' : 'center'}
                vertical={screenType === 'mobile'}
                style={{ width: '100%' }}
              >
                <span className="medium-text">
                  {translate('Mini statement').toUpperCase()}
                </span>
                <Flex
                  gap="small"
                  wrap={screenType === 'mobile'}
                  style={{
                    width: screenType === 'mobile' ? '100%' : 'auto',
                  }}
                >
                  <Button
                    variant="filled"
                    onClick={onOpenAccountDetails}
                    style={{
                      width:
                        screenType === 'mobile' ? '100%' : 'auto',
                    }}
                  >
                    {translate('Account Details')}
                  </Button>
                  <Button
                    variant="filled"
                    onClick={onOpenGetStatement}
                    style={{
                      width:
                        screenType === 'mobile' ? '100%' : 'auto',
                    }}
                  >
                    {translate('Account statement')}
                  </Button>
                  <Button
                    type="primary"
                    icon={<RightOutlined />}
                    onClick={() => navigate(urls.transactions)}
                    style={{
                      width:
                        screenType === 'mobile' ? '100%' : 'auto',
                    }}
                  >
                    {translate('See all')}
                  </Button>
                </Flex>
              </Flex>
            </Flex>

            <div className="table-container">
              <Table
                size="small"
                loading={loadTransaction}
                columns={columns}
                dataSource={filterTransactionsData || []}
                className="transaction-table"
                scroll={{
                  x: 'max-content',
                  y: 400,
                }}
                pagination={false}
                locale={{
                  emptyText: (
                    <Flex
                      gap="middle"
                      align="center"
                      justify="center"
                      style={{
                        width: '100%',
                        height: '30vh',
                      }}
                      wrap
                    >
                      <Empty
                        imageStyle={{ height: 100 }}
                        description={
                          <Typography.Text className="medium-text">
                            {translate(
                              'No external transactions found',
                            )}
                          </Typography.Text>
                        }
                      />
                    </Flex>
                  ),
                }}
              />
            </div>
          </Flex>
        </Flex>

        {screenType === 'mobile' && <Divider />}
      </Flex>
    </Card>

    <Card
      size="small"
      className="border-grey-color insights-card"
      style={{ width: screenType === 'mobile' ? '100%' : '25%' }}
    >
      <Flex
        gap="large"
        align="start"
        vertical
        style={{ width: '100%' }}
      >
        <Flex
          align="center"
          justify="space-between"
          style={{ width: '100%' }}
        >
          <span className="insights-title">
            {translate(`${MonthName}`).toLocaleUpperCase()}{' '}
            {translate('INSIGHTS')}
          </span>
          <Button
            type="text"
            onClick={
              isInSightBalanceVisible
                ? handleToggleHideInsight
                : onHandleOpenModal
            }
            icon={
              isInSightBalanceVisible ? (
                <EyeOutlined />
              ) : (
                <EyeInvisibleOutlined />
              )
            }
          />
        </Flex>
        <Flex gap="large" style={{ width: '100%' }} vertical>
          <AccountInsights
            title={translate('in').toLocaleUpperCase()}
            cardColor="green"
            TotalAmount={`${transactionSummaryData?.TotalAmountIn}`}
            isBalanceVisible={isInSightBalanceVisible}
            AccountCurrency={transactionSummaryData?.AccountCurrency}
          />
          <AccountInsights
            title={translate('out').toLocaleUpperCase()}
            cardColor="volcano"
            TotalAmount={`${transactionSummaryData?.TotalAmountOut}`}
            isBalanceVisible={isInSightBalanceVisible}
            AccountCurrency={transactionSummaryData?.AccountCurrency}
          />
        </Flex>
      </Flex>
    </Card>
  </Flex>
);

export default Account;
